import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NbDialogModule, NbTooltipModule } from '@nebular/theme';
import { NgbModalModule } from '@ng-bootstrap/ng-bootstrap';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { NgxSpinnerModule } from 'ngx-spinner';
import { MaterialModule } from '../../material.module';
import { NebularModule } from '../../nebular.module';
import { AddUserSignupComponent } from './add-user-signup/add-user-signup.component';
import { AdminChartsComponent } from './admin-charts/admin-charts.component';
import { ChartComponent } from './admin-charts/components/chart/chart.component';
import { PerformanceUsageChartComponent } from './admin-charts/components/performance-usage-chart/performance-usage-chart.component';
import { AdminEventFormsComponent } from './admin-event-forms/admin-event-forms.component';
import { AdminSchoolEventsComponent } from './admin-school-events/admin-school-events.component';
import { ChangeThemeComponent } from './change-theme/change-theme.component';
import { DesmosGraphComponent } from './desmos-graph/desmos-graph.component';
import { ForgotPasswordComponent } from './forgot-password/forgot-password.component';
import { InstructionalPeriodsComponent } from './instructional-periods/instructional-periods.component';
import { LessonPlanComponent } from './lesson-plan/lesson-plan.component';
import { PacingPlanDayViewComponent } from './pacing-plan-day-view/pacing-plan-day-view.component';
import { PacingPlansMonthViewComponent } from './pacing-plan-month-view/pacing-plan-month-view.component';
import { PowerUpPointsComponent } from './power-up-points/power-up-points.component';
import { NumberToPipe, SearchPipe, SecondsToMinutesSecondsPipe, searchSectionTitlePipe } from './search.pipe';
import { StudentPreviousLessonsComponent } from './student-previous-lessons/student-previous-lessons.component';
import { StudentsExtraHelpComponent } from './students-extra-help/students-extra-help.component';
import { StudentsUpcomingAssignmentsComponent } from './students-upcoming-assignments/students-upcoming-assignments.component';
import { SuperAdminPacingPlanComponent } from './super-admin-pacing-plan/super-admin-pacing-plan.component';
import { TeacherAcademicInfoComponent } from './teacher-academic-info/teacher-academic-info.component';
import { TeacherPeroidsComponent } from './teacher-peroids/teacher-peroids.component';
import { TeacherProfilePortalComponent } from './teacher-profile-portal/teacher-profile-portal.component';
import { TeachersListComponent } from './teachers-list/teachers-list.component';
import { DocumentsPipe, UserSearchPipe } from './user-search.pipe';
import { VideoTutorialsComponent } from './video-tutorials/video-tutorials.component';
import { ViewRecommendationTemplateComponent } from './view-recommendation-template/view-recommendation-template.component';

import { MathjaxModule } from 'mathjax-angular';
import { NgxDocViewerModule } from 'ngx-doc-viewer';
import { NgxEchartsModule } from 'ngx-echarts';
import { IConfig, NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { FormStepperComponent } from './form-stepper/form-stepper.component';
import { GlobalAssistantComponent } from './global-assistant/global-assistant.component';
import { MatDialogComponent } from './mat-dialog/mat-dialog.component';
import { MobileCompatibilityCheckComponent } from './mobile-compatibility-check/mobile-compatibility-check.component';
import { NotFoundComponent } from './not-found/not-found.component';
import { RenamePdfDialogComponent } from './rename-pdf-dialog/rename-pdf-dialog.component';
import { SignupActivityComponent } from './signup-activity/signup-activity.component';
import { TeacherDashboardHelpRequestsComponent } from './students-extra-help/components/teacher-dashboard-help-requests/teacher-dashboard-help-requests.component';
import { TeacherFeedbackComponent } from './teacher-feedback/teacher-feedback.component';
import { YouTubePlayerComponent } from './you-tube-player/you-tube-player.component';
export const options: Partial<null | IConfig> | (() => Partial<IConfig>) = null;

@NgModule({
  declarations: [
    ForgotPasswordComponent,
    AdminSchoolEventsComponent,
    AdminEventFormsComponent,
    TeacherAcademicInfoComponent,
    InstructionalPeriodsComponent,
    TeacherPeroidsComponent,
    SearchPipe,
    NumberToPipe,
    SecondsToMinutesSecondsPipe,
    UserSearchPipe,
    searchSectionTitlePipe,
    TeachersListComponent,
    DocumentsPipe,
    SuperAdminPacingPlanComponent,
    InstructionalPeriodsComponent,
    StudentPreviousLessonsComponent,
    StudentsUpcomingAssignmentsComponent,
    StudentsExtraHelpComponent,
    PowerUpPointsComponent,
    AddUserSignupComponent,
    PowerUpPointsComponent,
    AddUserSignupComponent,
    AdminChartsComponent,
    ChartComponent,
    PerformanceUsageChartComponent,
    ChangeThemeComponent,
    PacingPlansMonthViewComponent,
    PacingPlanDayViewComponent,
    VideoTutorialsComponent,
    ViewRecommendationTemplateComponent,
    LessonPlanComponent,
    TeacherProfilePortalComponent,
    DesmosGraphComponent,
    FormStepperComponent,
    TeacherDashboardHelpRequestsComponent,
    MatDialogComponent,
    GlobalAssistantComponent,
    SignupActivityComponent,
    TeacherFeedbackComponent,
    MobileCompatibilityCheckComponent,
    NotFoundComponent,
    YouTubePlayerComponent,
    RenamePdfDialogComponent,
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NebularModule,
    NgxSpinnerModule,
    NbDialogModule,
    MaterialModule,
    CalendarModule.forRoot({
      provide: DateAdapter,
      useFactory: adapterFactory,
    }),
    HttpClientModule,
    NgbModalModule,
    NgxChartsModule,
    NgxMatSelectSearchModule,
    NgxEchartsModule.forRoot({
      echarts: () => import('echarts'),
    }),
    NgxMaskPipe,
    NgxMaskDirective,
    NgxDocViewerModule,
    NbTooltipModule,
    MathjaxModule.forRoot({
      config: {
        loader: { load: ['input/tex', 'output/svg'] },
        tex: {
          inlineMath: [
            // ['$', '$'],
            ['\\(', '\\)'],
          ],
          displayMath: [
            // ['$$', '$$'],
            ['\\[', '\\]'],
          ],
          packages: ['base', 'ams'], // Add additional packages if you need them,
          processEscapes: true,
          ignoreHtmlClass: 'mathjax-ignore',
          options: {
            skipHtmlTags: ['[data-mathjax-ignore]'],
          },
        },
      },
    }),
  ],
  exports: [
    ForgotPasswordComponent,
    AdminSchoolEventsComponent,
    AdminEventFormsComponent,
    TeacherAcademicInfoComponent,
    InstructionalPeriodsComponent,
    TeacherPeroidsComponent,
    SearchPipe,
    NumberToPipe,
    SecondsToMinutesSecondsPipe,
    searchSectionTitlePipe,
    UserSearchPipe,
    TeachersListComponent,
    SuperAdminPacingPlanComponent,
    DocumentsPipe,
    StudentPreviousLessonsComponent,
    StudentsUpcomingAssignmentsComponent,
    ChangeThemeComponent,
    PowerUpPointsComponent,
    AddUserSignupComponent,
    PacingPlansMonthViewComponent,
    PacingPlanDayViewComponent,
    AdminChartsComponent,
    ChartComponent,
    PerformanceUsageChartComponent,
    VideoTutorialsComponent,
    ViewRecommendationTemplateComponent,
    LessonPlanComponent,
    TeacherProfilePortalComponent,
    DesmosGraphComponent,
    StudentsExtraHelpComponent,
    FormStepperComponent,
    TeacherDashboardHelpRequestsComponent,
    MatDialogComponent,
    GlobalAssistantComponent,
    TeacherFeedbackComponent,
    MobileCompatibilityCheckComponent,
    NotFoundComponent,
    YouTubePlayerComponent,
    RenamePdfDialogComponent,
  ],

  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [provideNgxMask()],
})
export class SharedModule {}
